import { useState } from "react";
import { logger } from "@/lib/logger";
import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";

/**
 * Sticky top promo strip with continuously scrolling marquee.
 * Dismissible per browser tab (sessionStorage).
 * Uses a shorter message on mobile so more of it fits on-screen.
 */
const PROMO = {
  messageDesktop:
    "WINTER PROTECTION OFFER — FREE Ceramic Wax on every Mini Valet & Full Valet, and 25% OFF Deep Clean Exterior. Applied automatically at checkout. Valid until 30 November.",
  messageMobile:
    "WINTER OFFER — FREE Ceramic Wax on Mini & Full Valet · 25% OFF Deep Clean Exterior — until 30 Nov",
  cta: "Book Now",
  href: "/booking",
  storageKey: "mobilwash_promo_bar_dismissed_v6",
  // Auto-hide the banner after this date (matches PROMO in the backend). Shown immediately — November bookings already qualify.
  endDate: "2026-11-30",
};

const isPromoExpired = () => {
  try {
    return new Date() > new Date(PROMO.endDate + "T23:59:59");
  } catch {
    return false;
  }
};

// Repeated pill used inside the marquee so the message reads continuously.
// Desktop shows the full message; mobile shows a concise version.
const MessageItem = () => (
  <>
    <span className="mx-3 sm:mx-6 inline-flex items-center gap-2 whitespace-nowrap">
      <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
      <span className="font-body font-semibold text-xs sm:text-base">
        {/* Mobile version */}
        <span className="sm:hidden">{PROMO.messageMobile}</span>
        {/* Desktop version */}
        <span className="hidden sm:inline">{PROMO.messageDesktop}</span>
      </span>
      <span className="mx-1 sm:mx-3 opacity-40">•</span>
    </span>
  </>
);

const PromoBar = () => {
  // sessionStorage only remembers "banner dismissed" for this tab — nothing sensitive is stored
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(PROMO.storageKey) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;
  if (isPromoExpired()) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(PROMO.storageKey, "1"); } catch (err) { logger.warn('sessionStorage unavailable; promo dismissal will not persist', err); }
    setDismissed(true);
  };

  // Duplicate the block so the animation loops seamlessly
  const items = Array.from({ length: 10 });

  return (
    <div
      className="relative z-[60] bg-gradient-to-r from-teal-500 via-teal-400 to-teal-500 text-black border-b border-teal-300/40 overflow-hidden"
      data-testid="promo-bar"
    >
      <div className="flex items-center gap-2 py-2 sm:py-2.5">
        {/* Scrolling marquee track */}
        <div className="flex-1 overflow-hidden">
          <div className="promo-marquee flex whitespace-nowrap">
            {items.map((_, i) => <MessageItem key={`a-${i}`} />)}
            {items.map((_, i) => <MessageItem key={`b-${i}`} />)}
          </div>
        </div>

        <Link
          to={PROMO.href}
          data-testid="promo-bar-cta"
          className="hidden sm:inline-flex flex-shrink-0 mr-2 px-3 py-1 rounded-full bg-black text-teal-300 font-bold text-xs uppercase tracking-wider hover:bg-gray-900 transition-colors whitespace-nowrap"
        >
          {PROMO.cta}
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss promo"
          data-testid="promo-bar-dismiss"
          className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full hover:bg-black/10 transition mr-1 sm:mr-2"
        >
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>
      </div>

      <style>{`
        @keyframes promo-marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .promo-marquee {
          animation: promo-marquee-scroll 10s linear infinite;
          will-change: transform;
        }
        @media (max-width: 640px) {
          .promo-marquee {
            animation-duration: 6s;
          }
        }
        .promo-marquee:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .promo-marquee { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default PromoBar;
